import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Spinner, ChatCircleText, Sparkle, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Teacher view of anonymous post-class feedback. Renders:
 *  - the aggregate (average, total, star distribution)
 *  - segmentation by CEFR level, learning goal, learning pace
 *  - chronological list of individual rating + comment rows
 *
 * The backend strips `bucket` (the one-way hash) before returning, so even
 * a curious teacher cannot reverse-engineer which student wrote what.
 */
export default function ClassFeedbackInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [aiSending, setAiSending] = useState(false);

  const sendAiSummary = async () => {
    setAiSending(true);
    try {
      const res = await axios.post(`${API}/classes/feedback/summary`, { days }, { withCredentials: true });
      if (res.data?.ok) {
        toast.success(`KI-Auswertung an ${res.data.recipient} gesendet (${res.data.total} Antworten analysiert).`, { duration: 7000 });
      } else if (res.data?.reason === 'no_feedback') {
        toast.message('Noch keine Antworten in diesem Zeitraum — nichts zu senden.');
      } else {
        toast.error('E-Mail konnte nicht gesendet werden.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Fehler beim Senden';
      toast.error(detail);
    } finally {
      setAiSending(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/classes/feedback?days=${days}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner size={28} className="animate-spin text-[#AB1C24]" /></div>;
  }

  const items = data?.items || [];
  const avg = data?.average;
  const byRating = data?.by_rating || {};
  const byLevel = data?.by_level || {};
  const byGoal = data?.by_goal || {};
  const byPace = data?.by_pace || {};

  const niceLabel = (k) => ({
    travel: 'Reisen', exam: 'Prüfung', intensive: 'Intensiv', relaxed: 'Entspannt',
    unbekannt: 'Ohne Angabe',
  }[k] || k);

  return (
    <div className="space-y-6" data-testid="class-feedback-insights">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-lg text-[#292524]">Anonymes Klassen-Feedback</h3>
          <p className="text-sm text-[#78716C]">
            Was deine Schüler nach den Online-Klassen sagen — vollständig anonym, segmentiert nach Niveau, Ziel und Tempo.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-sm border border-stone-300 rounded-lg px-3 py-1.5 bg-white"
            data-testid="feedback-range-select"
          >
            <option value={7}>Letzte 7 Tage</option>
            <option value={30}>Letzte 30 Tage</option>
            <option value={90}>Letzte 90 Tage</option>
            <option value={365}>Letztes Jahr</option>
          </select>
          {items.length > 0 && (
            <button
              onClick={sendAiSummary}
              disabled={aiSending}
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#AB1C24] to-[#7a131a] hover:from-[#8A151C] hover:to-[#5d0e14] disabled:opacity-60 text-white px-3 py-1.5 rounded-lg text-sm font-semibold shadow-sm transition-all"
              title="KI analysiert das Feedback und schickt dir die Auswertung per E-Mail"
              data-testid="ai-feedback-summary-btn"
            >
              {aiSending
                ? <><Spinner size={14} className="animate-spin" /> Sende…</>
                : <><Sparkle size={14} weight="fill" /> KI-Auswertung per E-Mail <EnvelopeSimple size={14} /></>}
            </button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-stone-50 border border-dashed border-stone-300 rounded-xl p-8 text-center text-[#78716C]">
          <ChatCircleText size={40} weight="duotone" className="mx-auto mb-2 text-stone-400" />
          <p>Noch kein Feedback in diesem Zeitraum. Sobald deine Schüler eine Live-Klasse verlassen, sehen sie automatisch das Feedback-Pop-up.</p>
        </div>
      ) : (
        <>
          {/* Top summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-[#AB1C24] to-[#7a131a] rounded-2xl p-5 text-white">
              <p className="text-xs uppercase tracking-wider opacity-80">Durchschnitt</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-4xl font-bold">{avg?.toFixed(2) ?? '—'}</span>
                <span className="text-sm opacity-80">/ 5</span>
              </div>
              <div className="flex items-center gap-0.5 mt-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} size={16} weight={i <= Math.round(avg || 0) ? 'fill' : 'regular'} className={i <= Math.round(avg || 0) ? 'text-yellow-300' : 'text-white/30'} />
                ))}
              </div>
              <p className="text-xs opacity-80 mt-3">{data.total} Antworten insgesamt</p>
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl p-5 col-span-2">
              <p className="text-xs uppercase tracking-wider font-bold text-[#78716C] mb-2">Bewertungs­verteilung</p>
              <div className="space-y-1.5">
                {[5, 4, 3, 2, 1].map(r => {
                  const n = byRating[String(r)] || 0;
                  const pct = data.total ? Math.round((n / data.total) * 100) : 0;
                  return (
                    <div key={r} className="flex items-center gap-2 text-sm">
                      <span className="w-12 flex items-center gap-0.5">
                        <span className="font-bold">{r}</span>
                        <Star size={12} weight="fill" className="text-yellow-500" />
                      </span>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#AB1C24]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-12 text-right text-xs text-[#78716C]">{n} · {pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Segmentation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Nach Niveau', data: byLevel },
              { title: 'Nach Lernziel', data: byGoal },
              { title: 'Nach Lerntempo', data: byPace },
            ].map(seg => (
              <div key={seg.title} className="bg-white border border-stone-200 rounded-2xl p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-[#78716C] mb-2">{seg.title}</p>
                <ul className="space-y-1 text-sm">
                  {Object.entries(seg.data).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                    <li key={k} className="flex items-center justify-between gap-2">
                      <span className="text-[#292524]">{niceLabel(k)}</span>
                      <span className="font-bold text-[#AB1C24]">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Individual rows */}
          <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
            <div className="px-4 py-3">
              <p className="text-xs uppercase tracking-wider font-bold text-[#78716C]">Einzel-Antworten ({items.length})</p>
            </div>
            {items.map(it => (
              <div key={it.feedback_id} className="px-4 py-3" data-testid={`feedback-row-${it.feedback_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star key={i} size={14} weight={i <= it.rating ? 'fill' : 'regular'} className={i <= it.rating ? 'text-yellow-500' : 'text-stone-300'} />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold flex-wrap">
                    {it.audience_level && <span className="bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">{it.audience_level}</span>}
                    {it.audience_goal === 'travel' && <span className="bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">Reisen</span>}
                    {it.audience_goal === 'exam' && <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">Prüfung</span>}
                    {it.audience_pace === 'intensive' && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Intensiv</span>}
                    {it.audience_pace === 'relaxed' && <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Entspannt</span>}
                    <span className="text-stone-400 normal-case font-normal">{it.class_date}</span>
                  </div>
                </div>
                {it.comment ? (
                  <p className="text-sm text-[#292524] whitespace-pre-wrap leading-snug">{it.comment}</p>
                ) : (
                  <p className="text-xs text-stone-400 italic">Ohne Kommentar</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
